import React from 'react';
import PropTypes from 'prop-types';
import CwTriggerField from '../cwtriggerfield/CwTriggerField';
import Calendar from './Calendar';
import css from './CwDatePicker.scss';
import changeCheck from '../../utils/ChangeCheck';
import { setMenuPositions } from '../../utils/CwDropdownService';
import moment from 'moment';
import IconPath from 'src/utils/IconPath';
import { extractDate } from 'src/utils/DateUtils';

class CwDatePicker extends React.Component {

  constructor(props) {
    super(props);
    moment.locale(this.props.locale);

    const dateOnly = props.value ? extractDate(props.value, props.isDateTime) : undefined;
    
    this.state = {
      date: props.value ? props.value : moment(),
      prevDate: moment(),
      childVisible: false,
      hasFocus: false,
      display: props.value ? this.props.formatter(props.isDateTime ? props.value : moment(dateOnly)) : '',
    };

    this._typing = false;
    this._ignoreBlur = false;
    this.mouseIsDownOnCalendar = false;
    this.formats = ['YYYY-MM-DDTHH:MM:SSZ', moment.localeData().longDateFormat('L'), moment.localeData().longDateFormat('l'), moment.localeData().longDateFormat('LL'),
      moment.localeData().longDateFormat('ll'), moment.localeData().longDateFormat('LLL'), moment.localeData().longDateFormat('LLLL'),
      moment.localeData().longDateFormat('lll'), moment.localeData().longDateFormat('llll')];
    this.keyDownHandlers = {
      ArrowDown(event) {
        event.preventDefault(); // prevent window scrolling
        this.expandChild();
      },

      Enter(event) {
        this._typing = false;
        //Force a blur on enter then regain focus. 
        this.handleBlur(event);
        this.setState({
          hasFocus: true,
        });
      },

      Escape() {
        this.collapseChild();
      }
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    const propsToCheck = ['value', 'disabled', 'invalid'];
    const stateToCheck = ['date', 'childVisible', 'hasFocus', 'display'];
    return changeCheck(propsToCheck, this.props, nextProps) || changeCheck(stateToCheck, this.state, nextState) || (this.state.prevDate !== nextState.date);
  };

  componentWillReceiveProps(nextProps) {
    const dateOnly = nextProps.value ? extractDate(nextProps.value, this.props.isDateTime) : undefined;

    if (nextProps.value && this._typing === true) {
      this.setState({
        date: this.props.isDateTime ? moment(dateOnly) : moment.utc(dateOnly),
        prevDate: this.props.value,
      });
    } else if (nextProps.value && this._typing === false) {
      this.setState({
        date: this.props.isDateTime ? moment(dateOnly) : moment.utc(dateOnly),
        prevDate: this.props.value,
        display: this.props.formatter(this.props.isDateTime ? nextProps.value : moment(dateOnly))
      });
    } else if (!this.state.hasFocus) {
      this.setState({
        display: '',
      });
    }
  }

  expandChild() {
    if(!this.state.childVisible) {
      this.setState({
        childVisible: true
      });
    }
  }

  collapseChild() {
    if(this.state.childVisible) {
      this.setState({
        childVisible: false
      });
    }
  }

  setIgnoreBlur = (ignore) => {
    this._ignoreBlur = ignore;
  };

  /**
   *  Let's set up the mouse events so we can listen for clicks outside the calendar
   */
  componentDidMount = () => {
    window.addEventListener('mousedown', this.pageClick, false);
    window.addEventListener('scroll', this.handleScroll, true);
  };

  componentDidUpdate = (prevProps, prevState) => {
    if (this.state.childVisible === true && prevState.childVisible === false) {
      const menuPositions = setMenuPositions(this.cwTriggerField.cwTextField.inputField);
      const node = this.refs.Calendar.div;

      this.setState({
        menuTop: menuPositions.menuTop,
        menuLeft: menuPositions.menuLeft,
        inputHeight: menuPositions.inputHeight,
        node: node
      });
    }
  };

  handleScroll = () => {
    /* istanbul ignore next */
    if (this.mouseIsDownOnCalendar) {
      return;
    }

    this.collapseChild();
  };

  pageClick = () => {
    /* istanbul ignore next */
    if (this.mouseIsDownOnCalendar) {
      return;
    }
    this.collapseChild();
  };

  mouseDownHandler = (blur) => {
    this.mouseIsDownOnCalendar = true;
    this.setIgnoreBlur(blur);
  };

  mouseUpHandler = () => {
    this.mouseIsDownOnCalendar = false;
  };

  componentWillUnmount() {
    window.removeEventListener('mousedown', this.pageClick);
    window.removeEventListener('scroll', this.handleScroll, true);
  };

  /**
   * We'll use this method to set the working date, note that this isn't what's displayed in the textfield, but it's
   * how the header knows what month and year we're on. We'll also toggle the visibility of the calendar itself
   * and pass that values that are needed up the chain to update the text field.
   */
  onValueChange = (date) => {
    const dateOnly = date ? extractDate(date, this.props.isDateTime) : undefined;    
    const invalid = date === null ? true : !date.isValid();

    this.setState({
      date: invalid ? moment() : date,
      display: invalid ? '' : this.props.formatter(this.props.isDateTime ? date : moment(dateOnly)),
      hasFocus: false,
    });
    this.collapseChild();
    if (invalid) {
      this.props.onChange(null);
    } else {
      this.props.onChange(date);
    }
  };

  handleTyping = (value) => {
    if (value || value === '') {
      this.setState({
        display: value,
        hasFocus: true,
      });
    }
    if (value) {
      const date = this.props.isDateTime ? moment(value, this.formats, this.props.locale) : moment.utc(value, this.formats, this.props.locale);
      if (date) {
        if (date.isValid()) {
          const invalid = date === null ? true : !date.isValid();
          this.setState({
            date: invalid ? moment() : date,
          });

          if (invalid) {
            this.props.onChange(null);
          } else {
            this.props.onChange(date);
          }
          return;
        }
      }
    }
    this.props.onChange(null);

  };

  //Toggles the visibility of the calendar.
  onTriggerClicked = () => {
    this.setState((prevState) => {
      return { childVisible: !prevState.childVisible };
    });
  };

  /**
   * Format the date object to a string on blur and set the Display value to that string
   */
  handleBlur = () => {
    this._typing = false;
    if (this._ignoreBlur)
      return;
    this.collapseChild();
    const date = moment(this.state.display, this.formats, this.props.locale);
    this.setState({
      display: date.isValid() ? this.props.formatter(date) : '',
      hasFocus: false,
    });

    if (this.props.onBlur) {
      this.props.onBlur();
    }
  };

  handleFocus = () => {
    this.setState({
      hasFocus: true,
    });
    if (this.props.onFocus) {
      this.props.onFocus();
    }
  };

  handleKeyDown = (event) => {
    this._typing = true;
    this.setIgnoreBlur(false);

    if (this.keyDownHandlers[event.key])
      this.keyDownHandlers[event.key].call(this, event);
  };

  /**
   * This method responds to the clicks of the left and right arrow, updating our working date time object without
   * updating the value in the displayed text field.
   */
  handleArrowClicks = (value) => {
    this.setState({
      date: value,
    });
  };

  getCalendarHeight = () => {
    if (this.state.node) {
      return this.state.node.clientHeight;
    } else {
      return 0;
    }
  }

  calcCalendarPosition = (settings) => {
    let top = settings.top;

    const spaceAbove = top - settings.inputHeight;
    const spaceBelow = window.innerHeight - top;

    // Calculate the menu height, use the max height if items is more than can fit on one page
    let menuHeight = this.getCalendarHeight();

    // If there's not enough room to fit the full menu below, make adjustments
    if (spaceBelow < menuHeight) {
      // Render above the component if there's enough space
      if (spaceAbove > menuHeight) {
        top = top - menuHeight - settings.inputHeight;
      }
      // If there's not enough space above but more space below, resize the menu
      /* istanbul ignore next */
      else if (spaceBelow > spaceAbove) {
        menuHeight = window.innerHeight - top;
      }
      // If there's more space above, resize the menu and display it above
      /* istanbul ignore next */
      else {
        menuHeight = top - settings.inputHeight;
        top = top - menuHeight - settings.inputHeight;
      }
    }

    return {
      top: top,
      menuHeight: menuHeight
    };
  }

  render() {

    const calendarSettings = {
      top: this.state.menuTop,
      inputHeight: this.state.inputHeight,
    };
    const dropdownPosition = this.calcCalendarPosition(calendarSettings);

    const style = { // Pass calculated position coordinates to calendar with inline style
      left: this.state.menuLeft,
      top: dropdownPosition.top,
    };

    return (
      <div data-cwid={this.props.cwId} onFocus={this.handleFocus} className={css.datePickerRoot}>
        <CwTriggerField
          cwId={this.props.cwId + '_trigger'}
          autoFocus={this.props.autoFocus}
          value={this.state.display}
          onFocus={this.handleFocus}
          triggerIcon={this.props.triggerIcon}
          onTrigger={this.onTriggerClicked}
          onBlur={this.handleBlur}
          onChange={this.handleTyping}
          onKeyDown={this.handleKeyDown}
          invalid={this.props.invalid}
          disabled={this.props.disabled}
          ref={(input) => this.cwTriggerField = input}
        />
        {
          this.state.childVisible ?
            <Calendar value={this.state.date}
              style={style}
              leftArrow={this.props.leftArrow}
              rightArrow={this.props.rightArrow}
              localToday={this.props.localToday}
              onChange={this.onValueChange}
              onMouseDown={this.mouseDownHandler}
              onMouseUp={this.mouseUpHandler}
              onHeaderClick={this.handleArrowClicks}
              isDateTime={this.props.isDateTime}
              ref={"Calendar"} />
            : null
        }
      </div>
    );
  }

}

CwDatePicker.propTypes = {
  /**
   * Connectwise element id to be used as unique identifier
   */
  cwId: PropTypes.string,
  /**
   *  Url of the text field trigger
   */
  triggerIcon: PropTypes.string,
  /**
   * The localized version of the word today
   */
  localToday: PropTypes.string,
  /**
   * url for the left arrow image
   */
  leftArrow: PropTypes.string,
  /**
   * url for the right arrow image
   */
  rightArrow: PropTypes.string,
  /**
   * An array of localized months
   */
  monthName: PropTypes.array,
  /**
   * An array of localized days of the week
   */
  daysOfTheWeek: PropTypes.array,
  /**
   * A function that formats the date time object. Should, at the very minmimum call the .toLocalDateString method
   * from the date object
   */
  formatter: PropTypes.func.isRequired,
  /**
   * Our initial date, can be null
   */
  value: PropTypes.oneOfType([
    PropTypes.instanceOf(moment),
    PropTypes.instanceOf(Date),
  ]),
  /**
   * A function to handle updating the text field should at least update the value
   */
  onChange: PropTypes.func,
  /**
   * Used to control if textfield is autofocused when the page loads
   */
  autoFocus: PropTypes.bool,
  /**
   * A prop to append to our internal onfocus
   */
  onFocus: PropTypes.func,
  /**
   * A prop to append to our internal onBlur
   */
  onBlur: PropTypes.func,
  /**
  * Used to control if date picker is valid
  */
  invalid: PropTypes.bool,
  /**
   * Used to disable date picker
   */
  disabled: PropTypes.bool,
  isDateTime: PropTypes.bool
};

CwDatePicker.defaultProps = {
  triggerIcon: IconPath.get('Action_DatePicker'),
  isDateTime: false
};

export default CwDatePicker;


// WEBPACK FOOTER //
// ./src/components/cwdatepicker/CwDatePicker.jsx